import {useEffect, useRef} from 'react';

import {useThemeColors} from '@nfq/colors';

import {isDefinedAndPopulated} from 'GlobalShared/utils/typeGuards';

import type {ImageUpload} from 'types/payload-types';

/**
 * The `useHero` hook manages the hero section state by providing theme colors, computing the product image aspect ratio, and auto-playing a video element.
 * It creates a reference to an HTML video element and triggers playback automatically when the component mounts.
 * Additionally, it retrieves the current theme color palette and calculates the aspect ratio from the product image dimensions, falling back to a 1:1 ratio if the image is not populated.
 *
 * @param productImage The product image upload object containing the image media data used to derive the aspect ratio for the hero section.
 * @returns An object containing `aspectRatio` as a CSS aspect-ratio string, `colors` with the current theme color palette, and `videoRef` as a React ref to attach to a video element for auto-playback.
 *
 * @example
 * ```tsx
 * const {aspectRatio, colors, videoRef} = useHero(productImage);
 *
 * return (
 *   <div style={{color: colors.primary, aspectRatio}}>
 *     <video ref={videoRef} src="hero.mp4" />
 *   </div>
 * );
 * ```
 */
export const useHero = (productImage: ImageUpload) => {
    const colors = useThemeColors();
    const videoRef = useRef<HTMLVideoElement>(null);
    let aspectRatio = '1 / 1';

    if (isDefinedAndPopulated(productImage.image)) {
        aspectRatio = `${productImage.image.width} / ${productImage.image.height}`;
    }

    useEffect(() => {
        if (videoRef.current) {
            void videoRef.current.play();
        }
    }, []);

    return {
        aspectRatio,
        colors,
        videoRef
    };
};